import { EMBER_ADDRESS, USDC } from '@emberswap/emberswap-sdk2/dist/constants'
import { Native } from '@emberswap/emberswap-sdk2/dist/entities'
import { ChainId, TradeType } from '@emberswap/emberswap-sdk2/dist/enums'
import { isChainSupported, tryParseAmount } from '@emberswap/emberswap-sdk2/dist/functions'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAddress } from 'viem'

import Chart from '../../components/Chart'
import { ChevronDownIcon, MaximizeIcon } from '../../components/Icons'
import { AddressInput } from '../../components/Layout/Account/AdressInput'
import { SettingsOverlay } from '../../components/SettingsOverlay'
import { SwapButton } from '../../components/SwapButton'
import { SwapStatsDisclosure } from '../../components/SwapStatsDisclosure'
import { ToastContainer } from '../../components/Toast'
import { TokenListImportChecker } from '../../components/TokenSelector'
import { TradeProvider } from '../../components/TradeProvider'
import { CurrencyInput } from '../../components/Web3Input/CurrencyInput'
import { Widget } from '../../components/Widget'
import { classNames } from '../../functions'
import { useIsMounted, usePrevious } from '../../hooks'
import { useTokens } from '../../hooks/Token/useTokens'
import { useSessionChainId } from '../../state/application/hooks'
import { useActivateChart } from '../../state/chart/hooks'
import { useCustomTokens, useSettings } from '../../state/user/hooks'



const getDefaultToken1 = (chainId: number) => {
  if (chainId in USDC) {
    return USDC[chainId]
  } else return
}

/**
 * Swap Page of the Application
 * @param {InferGetServerSidePropsType}initialState
 * @return {JSX.Element}
 */
export default function Swap(): JSX.Element {
  const isMounted = useIsMounted()
  // eslint-disable-next-line no-unused-vars
  const [chainId, { updateChainId }] = useSessionChainId()
  const tokenMap = useTokens(chainId)
  const [customTokensMap] =  useCustomTokens(chainId)
  const previousChain = usePrevious(chainId)
  const router = useRouter()
  const [recipientActive, setRecipientActive] = useState(false)
  const [{ userExpertMode }] = useSettings(chainId)
  const [active, , , { chartActive, toLiquidity }] = useActivateChart()

  // const deadBalance = useBalance({
  //   address: '0x000000000000000000000000000000000000dEaD' as Address,
  //   token: '0x217057A8B0bDEb160829c19243A2E03bfe95555a' as Address,
  //   chainId,
  //   enabled: true,
  //   watch: true,
  // })
  const inputToken = useMemo(() => {
    if (!chainId || !isMounted || Object.keys(tokenMap).length === 0) return
    const inputCurrency = router.query['inputCurrency'] as string
    if (inputCurrency && inputCurrency in tokenMap) return tokenMap[inputCurrency]
    if (inputCurrency && inputCurrency.toLowerCase() in customTokensMap)
      return customTokensMap[inputCurrency.toLowerCase()]
    if (
      inputCurrency &&
      inputCurrency.toLowerCase() === Native.onChain(chainId).symbol.toLowerCase()
    )
      return Native.onChain(chainId)
    return Native.onChain(chainId)
  }, [chainId, customTokensMap, isMounted, router.query, tokenMap])

  const outputToken = useMemo(() => {
    if (!chainId || !isMounted || Object.keys(tokenMap).length === 0) return
    const outputCurrency = router.query['outputCurrency'] as string
    if (outputCurrency && outputCurrency in tokenMap) return tokenMap[outputCurrency]
    if (outputCurrency && outputCurrency.toLowerCase() in customTokensMap)
      return customTokensMap[outputCurrency.toLowerCase()]
    if (
      outputCurrency &&
      outputCurrency.toLowerCase() === Native.onChain(chainId).symbol.toLowerCase()
    )
      return Native.onChain(chainId)
    if (
      tokenMap &&
      EMBER_ADDRESS[chainId] &&
      Object.values(tokenMap).filter((t) => t.address === EMBER_ADDRESS[chainId])
    )
      return Object.values(tokenMap)
        .filter((t) => t.address === EMBER_ADDRESS[chainId])
        .at(0)
    return getDefaultToken1(chainId)
  }, [chainId, customTokensMap, isMounted, router.query, tokenMap])

  const [[token0, token1], setTokens] = useState<[Type | undefined, Type | undefined]>([
    inputToken,
    outputToken,
  ])

  const [input0, setInput0] = useState<string>(
    !isNaN(Number(router.query['input0'])) ? router.query['input0'] as string : ''
  )
  const [input1, setInput1] = useState<string>('')
  const [tradeType, setTradeType] = useState<TradeType>(TradeType.EXACT_INPUT)

  const wrap = Boolean(
    token0 && token1 && token0.isNative && token1.equals(Native.onChain(token1.chainId).wrapped)
  )
  const unwrap = Boolean(
    token0 && token1 && token1.isNative && token0.equals(Native.onChain(token0.chainId).wrapped)
  )
  const isWrap = wrap || unwrap

  const [parsedInput0, parsedInput1] = useMemo(() => {
    return [tryParseAmount(input0, token0), tryParseAmount(isWrap ? input0 : input1, token1)]
  }, [input0, input1, isWrap, token0, token1])

  const amounts = useMemo(() => [parsedInput0], [parsedInput0])

  const checkIfImportedTokens = useMemo(() => {
    const tokens: { address: `0x${string}`; chainId: number }[] = []
    const inputCurrency = router.query['inputCurrency'] as string
    const outputCurrency = router.query['outputCurrency'] as string
    const rChainId =!isNaN(Number(router.query['chainId']))  ? Number(router.query['chainId']) : chainId
    if (inputCurrency && isAddress(inputCurrency))
      tokens.push({ address: inputCurrency, chainId: rChainId })
    if (outputCurrency && isAddress(outputCurrency))
      tokens.push({ address: outputCurrency, chainId: rChainId })
    return tokens
  }, [chainId, router.query])

  useEffect(() => {
    if (previousChain && chainId !== previousChain && chainId !== Number(router?.query?.chainId)) {
      // Clear up the query string if user changes network
      // whilst there is a chainId parameter in the query...
      delete router.query['chainId']
      delete router.query['inputCurrency']
      delete router.query['outputCurrency']
      delete router.query['input0']
      void router.replace(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined
      )
    }
    // Always do navigations after the first render
    // localhost:1337/swap?chainId=10001&inputCurrency=0x217057A8B0bDEb160829c19243A2E03bfe95555a&outputCurrency=BCHT&input0=12212
    // router.replace(`/swap?chainid=${chainId}&inputCurrency={}`, undefined, { shallow: true })
    if (
      isChainSupported(Number(router.query['chainId'])) &&
      Number(router.query['chainId']) !== chainId
    ) {
      updateChainId(Number(router.query['chainId']) as ChainId)
    }
  }, [router, chainId, previousChain, updateChainId])
  useEffect(() => {
    setTokens([inputToken, outputToken])
    setInput0(!isNaN(Number(router.query['input0'])) ? router.query['input0'] as string : '')
    setInput1('')
  }, [inputToken, outputToken, router.query])

  const onInput0 = useCallback((val: string) => {
    setTradeType(TradeType.EXACT_INPUT)
    setInput0(val)
  }, [])
  const onInput1 = useCallback((val: string) => {
    setTradeType(TradeType.EXACT_OUTPUT)
    setInput1(val)
  }, [])

  const _setToken0 = useCallback((currency: Type) => {
    setTokens(([prevSrc, prevDst]) => {
      return prevDst && currency.equals(prevDst) ? [prevDst, prevSrc] : [currency, prevDst]
    })
  }, [])
  const _setToken1 = useCallback((currency: Type) => {
    setTokens(([prevSrc, prevDst]) => {
      return prevSrc && currency.equals(prevSrc) ? [prevDst, prevSrc] : [prevSrc, currency]
    })
  }, [])
  const onSuccess = useCallback(() => {
    setInput0('')
    setInput1('')
  }, [])

  const switchCurrencies = useCallback(() => {
    // setInput1(TradeType.EXACT_INPUT && parsedInput0 ? parsedInput0?.toExact() : '')
    setTokens(([prevSrc, prevDst]) => [prevDst, prevSrc])
  }, [])
  return (
    <TokenListImportChecker chainId={chainId} tokens={checkIfImportedTokens}>
      <Head>
        <title>Swap | EmberSwap</title>
        <meta name="description" content="EmberSwap" />
      </Head>
      <TradeProvider
        chainId={chainId}
        tradeType={tradeType}
        amountSpecified={tradeType === TradeType.EXACT_INPUT ? parsedInput0 : parsedInput1}
        mainCurrency={token0}
        otherCurrency={token1}
      >
        <div className="w-screen p-4 max-w-screen">
          <div className="flex flex-wrap flex-shrink-0 w-full max-w-full gap-4 p-4 m-auto z-2 ">
            <Chart token={token1} chainId={chainId} />
            <Widget
              className="h-full flex-[1_1_300px] flex-shrink-0 min-w-[300px] !w-auto backdrop-blur-[1px] bg-swapwidget shadow shadow-shadowcolor"
              id="swap"
              maxWidth={500}
            >
              <Widget.Content>
                <div className="p-3 mx-0.5 grid grid-cols-2 items-center pb-4 font-medium mt-1">
                  <div className="flex gap-2">
                    <Link
                      href="http://localhost:1337/swap"
                      className={classNames(
                        router.pathname.includes('swap') ? 'text-slate-800' : 'text-slate-600',
                        'decoration-transparent text-sm font-semibold hover:text-slate-500 cursor-pointer'
                      )}
                      passHref
                    >
                      Swap
                    </Link>
                    <Link
                      onClick={() => {toLiquidity(true)}}
                      href="http://localhost:1337/liquidity"
                      className={classNames(
                        router.pathname.includes('liquidity') ? 'text-slate-800' : 'text-slate-600',
                        'decoration-transparent text-sm font-semibold hover:text-slate-500 cursor-pointer'
                      )}
                      passHref
                    >
                      Liquidity
                    </Link>
                  </div>
                  <div className="flex justify-end ml-3 -mt-1.5">
                    {!active && (
                      <div
                        onClick={() => chartActive(true)}
                        className="gap-x-1 px-2 text-slate-400 hover:text-slate-600 flex cursor-pointer text-xxs items-center  p-0 mx-auto! flex-[0_0_auto]   flex-wrap select-none border-2 border-transparent flex-shrink-0"
                      >
                        Activate Charts
                        <MaximizeIcon width={24} height={24} className="z-20 flex" />
                      </div>
                    )}
                    <SettingsOverlay userExpertMode={userExpertMode} chainId={chainId} />
                  </div>
                </div>
                <CurrencyInput
                  id={'swap-input-currency0'}
                  className="p-3"
                  value={input0}
                  onChange={onInput0}
                  currency={token0}
                  onSelect={_setToken0}
                  chainId={chainId}
                  inputType={TradeType.EXACT_INPUT}
                  tradeType={tradeType}
                  loading={!token0}
                />
                <div
                  className={classNames(
                    'grid items-center mx-auto grid-cols-1',
                    userExpertMode && !isWrap ? 'h-[30px]' : ''
                  )}
                >
                  <div
                    className={classNames(
                      'justify-center relative flex items-center transition  z-10',
                      userExpertMode && !isWrap ? '-mt-[24px] -mb-[72px]' : '-mt-[12px] -mb-[12px]'
                    )}
                  >
                    <button
                      type="button"
                      onClick={switchCurrencies}
                      className="group bg-slate-400/50 p-0.5 border-2 border-slate-300/50 transition-all rounded-full hover:ring-2 hover:ring-slate-500 cursor-pointer"
                    >
                      <div className="transition-all rotate-0 group-hover:rotate-180 group-hover:delay-200">
                        <ChevronDownIcon width={16} height={16} />
                      </div>
                    </button>
                  </div>
                  {userExpertMode && !isWrap && (
                    <div
                      onClick={() => setRecipientActive(!recipientActive)}
                      className="flex w-auto"
                    >
                      <div className="cursor-pointer z-[40] hover:text-slate-300 py-1 text-xs font-medium select-none text-slate-400 -mt-[24px] mb-[4px] ml-[14px] text-start">
                        {!recipientActive ? '+ Add Recipient (Optional)' : '- Remove Recipient'}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative border border-slate-600/20 ">
                  <CurrencyInput
                    id={'swap-output-currency1'}
                    // disabled={true}
                    className="p-3"
                    value={isWrap ? input0 : input1}
                    onChange={onInput1}
                    currency={token1}
                    onSelect={_setToken1}
                    chainId={chainId}
                    inputType={TradeType.EXACT_OUTPUT}
                    tradeType={tradeType}
                    loading={!token1}
                    isWrap={isWrap}
                  />
                  <AddressInput
                    isWrap={isWrap}
                    chainId={chainId}
                    userExpertMode={userExpertMode}
                    recipientActive={recipientActive}
                  >
                    {(_recipient) => {
                      return (
                        <>
                          <SwapStatsDisclosure chainId={chainId} />
                          <SwapButton
                            recipient={_recipient}
                            onSuccess={onSuccess}
                            recipientActive={recipientActive}
                            chainId={chainId}
                            parsedInput0={parsedInput0}
                            parsedInput1={parsedInput1}
                            amounts={amounts}
                            isWrap={isWrap}
                            wrap={wrap}
                          />
                        </>
                      )
                    }}
                  </AddressInput>
                </div>
              </Widget.Content>
            </Widget>
          </div>
        </div>
        <ToastContainer className="mt-[30px]" />
      </TradeProvider>
    </TokenListImportChecker>
  )
}
